<template>
    <div>
        <loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true" :height="150" :width="150"
            color="#FFFF00" background-color="#000000" :opacity="0.5" />

        <v-container>
            <v-form v-if="!isLoading">
                <!-- PAGE TITLE -->
                <v-row>
                    <h2 class="pb-5">
                        {{ $t('acknowledgement.acknowledgemenfor') }}
                        <span class="page_title_value">
                            {{ acknowledgementName }}
                        </span>
                    </h2>
                </v-row>

                <!-- BACK BUTTON -->
                <v-col class="mt-n4 mb-2">
                    <v-row>
                        <v-btn @click="backButton" color="orange" class="white--text">
                            {{ $t('button.back') }}
                        </v-btn>
                        <v-spacer></v-spacer>
                    </v-row>
                </v-col>

                <!-- PIE / DONUT CHART -->
                <v-row>
                    <v-col cols="12" md="6" offset-md="3">
                        <div class="chart-wrapper">
                            <apexchart ref="pieChart" type="donut" height="380" :options="chartOptions"
                                :series="chartSeries" @dataPointSelection="onSliceClick" />
                            <!-- HAMBURGER MENU -->
                            <v-menu offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn icon class="chart-menu" v-on="on">
                                        <v-icon>mdi-dots-vertical</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense>
                                    <v-list-item @click="downloadChart('svg')">
                                        <v-list-item-title>Download SVG</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="downloadChart('png')">
                                        <v-list-item-title>Download PNG</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="exportCSV">
                                        <v-list-item-title>Download CSV</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </v-col>
                </v-row>

                <!-- SUMMARY ROW -->
                <v-row class="mt-4 mb-2 text-center">
                    <v-col cols="12" md="4">
                        <v-card class="pa-4" outlined>
                            <strong>{{ $t('performancereport.totalstaff') }}</strong><br />
                            <span>{{ totalStaff }}</span>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-card class="pa-4" outlined>
                            <strong>{{ $t('acknowledgement.Accepted') }}</strong><br />
                            <span class="green--text">
                                {{ totalAccepted }} / {{ acceptedPercent }}%
                            </span>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-card class="pa-4" outlined>
                            <strong>{{ $t('columns.pending') }}</strong><br />
                            <span class="orange--text">
                                {{ totalPending }} / {{ pendingPercent }}%
                            </span>
                        </v-card>
                    </v-col>
                </v-row>
                <!-- <v-row class="align-center">
                    <v-col cols="12" class="d-flex justify-end align-center">
                        <v-avatar class="mr-3" size="40" tile>
                            <img :src="`${publicPath}images/export_pdf.png`" @click="printPDF">
                        </v-avatar>

                        <v-avatar class="mr-5" size="40" tile>
                            <img :src="`${publicPath}images/export_csv.png`" @click="exportCSV">
                        </v-avatar>

                        <v-text-field v-model="search" append-icon="search" label="Search" hide-details dense
                            style="max-width: 250px;"></v-text-field>
                    </v-col>
                </v-row> -->
                <v-row class="mt-n3">
                    <v-col cols="12">
                        <v-data-table :headers="headers" :items="filteredItems" :search="search"
                            :items-per-page="itemsPerPage" class="elevation-1">
                            <template v-slot:top>
                                <v-toolbar flat class="flex-column flex-md-row align-stretch">
                                    <!-- TITLE -->
                                    <v-toolbar-title class="flex-grow-1 mb-2 mb-md-0 text-truncate ml-4">
                                        {{ $t('acknowledgement.acknowledgementacceptancesummary') }}
                                        <span v-if="filterStatus !== 'All'"> – {{ filterStatus }}</span>
                                    </v-toolbar-title>
                                    <v-spacer></v-spacer>
                                    <!-- RIGHT CONTENT -->
                                    <div class="d-flex align-center flex-shrink-0">
                                        <v-avatar class="mr-2" size="40" tile>
                                            <img :src="`${publicPath}images/export_pdf.png`" @click="printPDF">
                                        </v-avatar>
                                        <v-avatar class="mr-3" size="40" tile>
                                            <img :src="`${publicPath}images/export_csv.png`" @click="exportCSV">
                                        </v-avatar>
                                        <v-text-field v-model="search" append-icon="mdi-magnify" label="Search"
                                            hide-details dense style="max-width: 250px; min-width: 200px;">
                                        </v-text-field>
                                    </div>
                                </v-toolbar>
                            </template>

                            <template v-slot:[`item.Status`]="{ item }">
                                <v-chip :color="item.Status === 'Accepted' ? 'green' : 'orange'" dark>
                                    {{ item.Status }}
                                </v-chip>
                            </template>

                            <template v-slot:[`item.acknowledgementDateTime`]="{ item }">
                                {{ formatDate(item.acknowledgementDateTime) }}
                            </template>
                        </v-data-table>
                    </v-col>
                </v-row>
            </v-form>

            <!-- ERROR DIALOG -->
            <div v-if="isErr">
                <app-error v-bind:parentError="{ data: this.errMsg, isErr: this.isErr }" />
            </div>
        </v-container>
    </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import ErrorDialog from "../../../components/common/ErrorDialog";
import VueApexCharts from "vue-apexcharts";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

export default {
    components: {
        Loading,
        "app-error": ErrorDialog,
        apexchart: VueApexCharts,
    },

    data() {
        return {
            publicPath: process.env.BASE_URL,
            id: this.$route.params.id,
            activestaff: this.$route.params.activestaff,
            acknowledgementName: "",
            dataItems: [],
            search: "",
            filterStatus: "All",
            isLoading: false,
            isErr: false,
            errMsg: "",
            itemsPerPage: 10,

            headers: [
                { text: this.$t('columns.status'), value: "Status" },
                { text: this.$t('login.username'), value: "userid" },
                { text: this.$t('columns.staffcode'), value: "staffcode" },
                { text: this.$t('columns.staffname'), value: "psnname" },
                { text: this.$t('attendancereportbypeiordbycompanyreport.companyname'), value: "compnyname" },
                { text: this.$t('acknowledgement.acknowledgedon'), value: "acknowledgementDateTime" },
            ],
        };
    },

    computed: {
        filteredItems() {
            if (this.filterStatus === "All") return this.dataItems;
            return this.dataItems.filter((x) => x.Status === this.filterStatus);
        },
        totalStaff() {
            return this.dataItems.length;
        },
        totalAccepted() {
            return this.dataItems.filter((x) => x.Status === "Accepted").length;
        },
        totalPending() {
            return this.dataItems.filter((x) => x.Status === "Pending").length;
        },
        acceptedPercent() {
            return this.totalStaff === 0
                ? 0
                : ((this.totalAccepted / this.totalStaff) * 100).toFixed(1);
        },
        pendingPercent() {
            return this.totalStaff === 0
                ? 0
                : ((this.totalPending / this.totalStaff) * 100).toFixed(1);
        },
        chartSeries() {
            return [this.totalAccepted, this.totalPending];
        },
        chartOptions() {
            const total = this.totalStaff || 1;
            return {
                chart: {
                    type: "donut",
                    toolbar: { show: false },
                    events: {
                        dataPointMouseEnter: () => {
                            document.querySelector('.apexcharts-canvas').style.cursor = 'pointer';
                        },
                        dataPointMouseLeave: () => {
                            document.querySelector('.apexcharts-canvas').style.cursor = 'default';
                        }
                    }
                },
                title: {
                    text: this.$t('acknowledgement.acknowledgementacceptancesummary'),
                    align: 'center',
                    style: {
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }
                },
                labels: ["Accepted", "Pending"],
                colors: ["#4CAF50", "#FFC107"],
                tooltip: {
                    y: {
                        formatter: (val, opts) => {
                            const seriesIndex = opts?.seriesIndex ?? 0;
                            // get label safely
                            const label = opts?.w?.globals?.labels?.[seriesIndex] ?? (seriesIndex === 0 ? 'Accepted' : 'Pending');
                            const count = val;
                            const percentage = label === "Accepted" ? this.acceptedPercent : this.pendingPercent;
                            // return `${label}: ${count} (${percentage}%)`;
                            return `${count} (${percentage}%)`; // only one 'label:' now
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: (val, opts) => {
                        const count = opts.w.config.series[opts.seriesIndex];
                        const percentage = (count / total * 100).toFixed(2);
                        return `${count} / ${percentage}%`;
                    },
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '60%',
                            labels: {
                                show: true,
                                name: { show: true },
                                value: { show: true },
                                total: {
                                    show: true,
                                    label: this.$t('performancereport.totalstaff'),
                                    formatter: () => this.totalStaff
                                }
                            }
                        }
                    }
                },
                legend: { position: 'bottom' },
            };
        },
    },

    methods: {
        backButton() {
            try {
                this.$router.push({ name: "acknowledgementacceptancelisting" });
            } catch {
                this.$router.go(-1);
            }
        },

        formatDate(date) {
            if (!date) return "-";
            return moment(date).format("YYYY-MM-DD HH:mm");
        },

        async loadacknowledgementDetails() {
            try {
                this.isLoading = true;
                const res = await this.$store.dispatch("post", {
                    url: "/acknowledgementacceptanceSummary",
                    payload: { id: this.id, activestaff: this.activestaff },
                });
                this.acknowledgementName = res.data.acknowledgement.ackName || "";
                this.dataItems = res.data.details || [];
            } catch (err) {
                this.isErr = true;
                this.errMsg = err;
            } finally {
                this.isLoading = false;
            }
        },

        onSliceClick(event, chartContext, config) {
            const clickedLabel = this.chartOptions.labels[config.dataPointIndex];
            this.filterStatus = this.filterStatus === clickedLabel ? "All" : clickedLabel;
        },

        exportCSV() {
            const csvRows = [];
            const headers = this.headers.map((h) => h.text);
            csvRows.push(headers.join(","));

            for (const item of this.filteredItems) {
                const row = [
                    item.Status,
                    item.userid,
                    item.staffcode,
                    item.psnname,
                    item.compnyname,
                    this.formatDate(item.acknowledgementDateTime),
                ];
                csvRows.push(row.join(","));
            }

            const safeName = this.acknowledgementName
                .substring(0, 40)
                .replace(/[^\w\s]/g, "");
            const statusSuffix = this.filterStatus === "All" ? "" : ` - ${this.filterStatus}`;
            const fileName = `Acknowledgement Acceptance Summary - ${safeName}${statusSuffix}.csv`;

            const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.setAttribute("download", fileName);
            link.click();
            URL.revokeObjectURL(url);
        },

        downloadChart(type) {
            const chart = this.$refs.pieChart.chart;
            chart.dataURI().then(({ imgURI, svg }) => {
                const data = type === "png" ? imgURI : svg;
                const link = document.createElement("a");
                link.href =
                    type === "png"
                        ? data
                        : "data:image/svg+xml;charset=utf-8," + encodeURIComponent(data);
                link.download = `acknowledgement_chart.${type}`;
                link.click();
            });
        },

        printPDF() {
            const doc = new jsPDF();
            const safeName = this.acknowledgementName
                .substring(0, 40)
                .replace(/[^\w\s]/g, "");
            const statusSuffix = this.filterStatus === "All" ? "" : ` - ${this.filterStatus}`;
            const fileName = `Acknowledgement Acceptance Summary - ${safeName}${statusSuffix}.pdf`;

            const headerTitle = `Acknowledgement Acceptance Summary – ${this.acknowledgementName}${this.filterStatus === "All" ? "" : ` (${this.filterStatus})`}`;
            doc.text(headerTitle, 14, 10);

            const tableData = this.filteredItems.map((item) => [
                item.Status,
                item.userid,
                item.staffcode,
                item.psnname,
                item.compnyname,
                this.formatDate(item.acknowledgementDateTime),
            ]);

            doc.autoTable({
                head: [this.headers.map((h) => h.text)],
                body: tableData,
                startY: 16,
            });

            doc.save(fileName);
        },
    },

    async mounted() {
        try {
            this.isLoading = true;
            await this.loadacknowledgementDetails();
        } catch (e) {
            this.isErr = true;
            this.errMsg = e.message || "Something went wrong during mounted";
        } finally {
            this.isLoading = false;
        }
    },
};
</script>

<style scoped>
.v-avatar img {
    cursor: pointer;
}

.v-card {
    text-align: center;
    font-size: 1.2em;
}

.chart-wrapper {
    position: relative;
    cursor: pointer;
}

.chart-menu {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 5;
}
</style>
